// App.js - Updated for Mobile OTP Authentication
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/styles.css';
import './assets/css/responsive.css';
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import HomeLayout from './components/layout/HomeLayout';
import Layout from './components/layout/Layout';

// Pages
import Home from './pages/Home';
import SignIn from './pages/auth/SignIn';
import VerifyOTP from './pages/auth/VerifyOTP';
// Remove SetupPassword import as it's not needed for OTP flow

// Dashboard pages
import StarRecDashboard from './pages/StarRec/Dashboard';
import StarRecApproveBusinesses from './pages/StarRec/businesses/Approve';
import StarRecManageBusinesses from './pages/StarRec/businesses/Manage';
import StarRecManageJobHolders from './pages/StarRec/job-holders/Manage';
import StarRecBlacklistBusinesses from './pages/StarRec/Blacklists/Businesses';
import StarRecBlacklistJobHolders from './pages/StarRec/Blacklists/Jobholders';
import StarRecBlacklistRequests from './pages/StarRec/Blacklists/Requests';
import StarRecAccounting from './pages/StarRec/Accounting';
import StarRecBusinessEntities from './pages/StarRec/Settings/BusinessEntities';
import StarRecBusinessCategories from './pages/StarRec/Settings/BusinessCategories';

import StardeasAnalytics from './pages/Stardeas/Analytics';
import StaglessAnalytics from './pages/Stagless/Analytics';
import FluxsterAnalytics from './pages/Fluxster/Analytics';
import BachLivAnalytics from './pages/Bachliv/Analytics';
import NearChatAnalytics from './pages/Nearchat/Analytics';

// Import authAPI for authentication
import { authAPI } from './api/apiConfig';

// ADMIN CREDENTIALS - Mobile OTP Authentication
const ADMIN_CREDENTIALS = {
  mobile: '+91-9490633344',
  otp: '123456'
};

// Clear authentication for testing - Remove this in production
const clearAuthForTesting = () => {
  // Uncomment this line to always start from login during development
  // authAPI.logout();
  // localStorage.clear();
};

// Check if user is authenticated with valid JWT token
const isValidAdmin = () => {
  return authAPI.isAuthenticated();
};

// Protected Route Component - Only for authenticated users
const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is authenticated
        const isAuth = isValidAdmin();
        setIsAuthenticated(isAuth);
        setLoading(false);
        
        // If not authenticated, clear invalid authentication
        if (!isAuth) {
          authAPI.logout();
        }
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Public Route Component (redirects to home if authenticated)
const PublicRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const isAuth = isValidAdmin();
        setIsAuthenticated(isAuth);
        setLoading(false);
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/home" replace />;
    }

    return children;
};

// Admin Verification Middleware
const AdminVerification = ({ children, pageType }) => {
  const [verifying, setVerifying] = useState(true);
  
  useEffect(() => {
    // For OTP page, check if mobile is admin
    if (pageType === 'otp') {
      const adminMobile = localStorage.getItem('adminMobile');
      if (adminMobile !== ADMIN_CREDENTIALS.mobile) {
        alert('Access Denied! Only admin can proceed.');
        window.location.href = '/';
        return;
      }
    }
    
    setVerifying(false);
  }, [pageType]);
  
  if (verifying) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verifying admin access...</span>
        </div>
      </div>
    );
  }
  
  return children;
};

// Logout Component
const Logout = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    authAPI.logout();
    navigate('/');
  }, [navigate]);

  return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Logging out...</span>
        </div>
        <p className="mt-3">Logging out...</p>
      </div>
    </div>
  );
};

export default function App() {
    // Clear auth on app load for testing
    useEffect(() => {
        clearAuthForTesting();
    }, []);

    return (
        <BrowserRouter>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                    <PublicRoute>
                        <SignIn />
                    </PublicRoute>
                } />
                
                <Route path="/verify-otp" element={
                    <PublicRoute>
                        <AdminVerification pageType="otp">
                            <VerifyOTP />
                        </AdminVerification>
                    </PublicRoute>
                } />
                
                {/* Logout route */}
                <Route path="/logout" element={<Logout />} />

                {/* Home Layout Routes */}
                <Route element={<HomeLayout />}>
                    <Route path="/home" element={
                        <ProtectedRoute>
                            <Home />
                        </ProtectedRoute>
                    } />
                </Route>

                {/* Dashboard Layout Routes */}
                <Route element={<Layout />}>
                    <Route path="/StarRec/dashboard" element={
                        <ProtectedRoute>
                            <StarRecDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/businesses/approve" element={
                        <ProtectedRoute>
                            <StarRecApproveBusinesses />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/businesses/manage" element={
                        <ProtectedRoute>
                            <StarRecManageBusinesses />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/job-holders/manage" element={
                        <ProtectedRoute>
                            <StarRecManageJobHolders />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/blacklists/businesses" element={
                        <ProtectedRoute>
                            <StarRecBlacklistBusinesses />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/blacklists/job-holders" element={
                        <ProtectedRoute>
                            <StarRecBlacklistJobHolders />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/blacklists/requests" element={
                        <ProtectedRoute>
                            <StarRecBlacklistRequests />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/accounting" element={
                        <ProtectedRoute>
                            <StarRecAccounting />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/settings/business-entities" element={
                        <ProtectedRoute>
                            <StarRecBusinessEntities />
                        </ProtectedRoute>
                    } />
                    <Route path="/StarRec/settings/business-categories" element={
                        <ProtectedRoute>
                            <StarRecBusinessCategories />
                        </ProtectedRoute>
                    } />

                    <Route path="/Stardeas/analytics" element={
                        <ProtectedRoute>
                            <StardeasAnalytics />
                        </ProtectedRoute>
                    } />
                    <Route path="/Stagless/analytics" element={
                        <ProtectedRoute>
                            <StaglessAnalytics />
                        </ProtectedRoute>
                    } />
                    <Route path="/Fluxster/analytics" element={
                        <ProtectedRoute>
                            <FluxsterAnalytics />
                        </ProtectedRoute>
                    } />
                    <Route path="/BachLiv/analytics" element={
                        <ProtectedRoute>
                            <BachLivAnalytics />
                        </ProtectedRoute>
                    } />
                    <Route path="/NearChat/analytics" element={
                        <ProtectedRoute>
                            <NearChatAnalytics />
                        </ProtectedRoute>
                    } />
                    
                    {/* Catch-all route for unauthorized access */}
                    <Route path="*" element={
                        <ProtectedRoute>
                            <Navigate to="/home" replace />
                        </ProtectedRoute>
                    } />
                </Route>
                
                {/* Catch-all route for public pages */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}

// (useNavigate imported above)